/**
 * Auto-print order tickets to a merchant's WiFi thermal printer via Star
 * CloudPRNT — the printer polls BookAPT's backend directly (see
 * backend/printing/registerRoutes.js), so no tablet or always-on computer
 * is needed at the store. This file has two jobs:
 *
 * 1. `queuePrintJobIfEnabled` — called right after a customer booking is
 *    confirmed (see OneClickBookingScreen.tsx), writes a ticket doc the
 *    printer will pick up on its next poll.
 * 2. `sendTestPrint` — the authenticated "Send test print" button in
 *    Settings, used to verify a newly-configured printer is wired up.
 */

import { Platform } from 'react-native';
import { WIDGET_BOOKING_API } from '../utils/widgetEmbed';

const API_BASE_URL = __DEV__ ? 'http://localhost:3000/api' : WIDGET_BOOKING_API;

async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    if (Platform.OS === 'web') {
      const { getWebAuth } = require('./unifiedAuthService');
      const auth = getWebAuth();
      const token = await auth?.currentUser?.getIdToken?.();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getAuth } = require('@react-native-firebase/auth');
    const token = await getAuth(getApp())?.currentUser?.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function sendTestPrint(
  businessId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/business/${encodeURIComponent(businessId)}/print-jobs/test`,
      { method: 'POST', headers: { 'Content-Type': 'application/json', ...headers } }
    );
    return await response.json();
  } catch (error: any) {
    return { success: false, error: error?.message || 'Could not send test print.' };
  }
}

/** Deterministic per-business CloudPRNT server URL — shown in Settings for provisioning. */
export function getPrintServerUrl(businessId: string): string {
  return `${API_BASE_URL}/print/${encodeURIComponent(businessId)}`;
}

/**
 * Reads printLastPolledAt directly from Firestore (the backend writes this
 * via Admin SDK on every poll — see backend/printing/registerRoutes.js — so
 * it's never reflected in the locally-cached AuthManager business owner
 * object; Settings needs a fresh read to show an accurate "last seen").
 */
export async function getPrintLastPolledAt(businessId: string): Promise<Date | null> {
  try {
    if (Platform.OS === 'web') {
      const { doc, getDoc } = require('firebase/firestore');
      const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
      if (!initializeFirebase() || !getWebDb()) return null;
      const db = getWebDb();
      const snap = await getDoc(doc(db, 'businesses', businessId));
      const ts = snap?.exists?.() ? snap.data()?.printLastPolledAt : null;
      return ts?.toDate?.() || null;
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, doc, getDoc } = require('@react-native-firebase/firestore');
    const db = getFirestore(getApp());
    const snap = await getDoc(doc(db, 'businesses', businessId));
    const ts = snap.exists ? snap.data()?.printLastPolledAt : null;
    return ts?.toDate?.() || null;
  } catch {
    return null;
  }
}

export interface PrintTicketInput {
  businessName?: string;
  customerName?: string;
  customerPhone?: string;
  serviceType?: string;
  specialInstructions?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  appointmentId?: string;
}

function formatTicketText(input: PrintTicketInput): string {
  const lines: string[] = [];
  lines.push(input.businessName || 'BookAPT Order');
  lines.push('--------------------------------');
  if (input.appointmentDate || input.appointmentTime) {
    lines.push(`${input.appointmentDate || ''} ${input.appointmentTime || ''}`.trim());
  }
  if (input.customerName) lines.push(`Customer: ${input.customerName}`);
  if (input.customerPhone) lines.push(`Phone: ${input.customerPhone}`);
  lines.push('');
  if (input.serviceType) lines.push(input.serviceType);
  if (input.specialInstructions) {
    lines.push('');
    lines.push(input.specialInstructions);
  }
  if (input.appointmentId) {
    lines.push('');
    lines.push(`Order #${input.appointmentId.slice(-8)}`);
  }
  lines.push('\n\n\n');
  return lines.join('\n');
}

/**
 * Queues a print job for a just-confirmed booking, if the business has
 * printing turned on. Fails silently (a print failure should never block a
 * booking) — writes directly via the client Firestore SDK, same access
 * pattern already used for appointments.
 */
export async function queuePrintJobIfEnabled(
  business: { id?: string; printingEnabled?: boolean } | null | undefined,
  input: PrintTicketInput
): Promise<void> {
  if (!business?.id || !business.printingEnabled) return;
  try {
    const ticketText = formatTicketText(input);
    if (Platform.OS === 'web') {
      const { collection, addDoc, serverTimestamp } = require('firebase/firestore');
      const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
      if (!initializeFirebase() || !getWebDb()) return;
      const db = getWebDb();
      await addDoc(collection(db, 'printJobs'), {
        businessId: business.id,
        status: 'pending',
        sourceType: 'order',
        sourceAppointmentId: input.appointmentId || null,
        ticketText,
        createdAt: serverTimestamp(),
      });
      return;
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, collection, addDoc, serverTimestamp } = require('@react-native-firebase/firestore');
    const db = getFirestore(getApp());
    await addDoc(collection(db, 'printJobs'), {
      businessId: business.id,
      status: 'pending',
      sourceType: 'order',
      sourceAppointmentId: input.appointmentId || null,
      ticketText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.warn('queuePrintJobIfEnabled failed (non-fatal):', error);
  }
}
